import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { getCashRegisterRepository } from '../app/di/container';
import sessionRecorder from '../utils/sessionRecorder';
import {
  pruneStaleWorkerSessions,
  reconcileAbandonedWorkerSessions
} from '../utils/workerSessionSanitizer';
import { getLotsForProduct, getProductQuantityOptimized, isLotAvailableForSale } from '../utils/lotSystem';

const NewCashRegisterContext = createContext();

export const useNewCashRegister = () => {
  const context = useContext(NewCashRegisterContext);
  if (!context) {
    throw new Error('useNewCashRegister must be used within a NewCashRegisterProvider');
  }
  return context;
};

export const NewCashRegisterProvider = ({ children }) => {
  const cashRegister = getCashRegisterRepository();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('login'); // 'login', 'logout', 'pause'
  const [pendingAction, setPendingAction] = useState(null);
  
  // Session state
  const [activeSession, setActiveSession] = useState(null);
  const [previousAmount, setPreviousAmount] = useState(null);
  
  // Business state
  const [businessId, setBusinessId] = useState(null);
  
  // Lot synchronization state
  const lotCacheRef = useRef(new Map());
  const [lastLotUpdate, setLastLotUpdate] = useState(null);

  // Initialize business ID
  useEffect(() => {
    const currentBusinessId = cashRegister.getCurrentBusinessId();
    setBusinessId(currentBusinessId);
  }, []);

  // Load active session when business ID changes
  useEffect(() => {
    if (businessId) {
      const session = cashRegister.getActiveSession(businessId);
      setActiveSession(session);
      
      if (!session) {
        const prevAmount = cashRegister.getPreviousSessionAmount(businessId);
        setPreviousAmount(prevAmount);
      }
    }
  }, [businessId]);

  // Lot synchronization - listen for lot updates
  useEffect(() => {
    const onDomainEvent = (event) => {
      const type = event?.detail?.type;
      if (
        type === 'inventory_updated' ||
        type === 'purchases_updated' ||
        type === 'sales_updated'
      ) {
        setLastLotUpdate(Date.now());
        lotCacheRef.current.clear();
      }
    };

    window.addEventListener('elkheir:domain-event', onDomainEvent);

    return () => {
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
    };
  }, []);

  // Open modal for login
  const openLoginModal = (workerId, workerName) => {
    // For login, we don't consider previous amount - worker enters current amount
    setPendingAction({
      type: 'login',
      workerId,
      workerName,
      previousAmount: null // Always null for login - worker enters current amount
    });
    setModalMode('login');
    setPreviousAmount(null); // Always null for login
    setIsModalOpen(true);
  };

  // Open modal for logout
  const openLogoutModal = (workerId, workerName) => {
    const session = cashRegister.getActiveSession(businessId);
    
    if (!session) {
      console.error('No active session found for logout');
      return;
    }

    setPendingAction({
      type: 'logout',
      workerId,
      workerName,
      sessionId: session.sessionId
    });
    setModalMode('logout');
    setIsModalOpen(true);
  };

  // Open modal for pause
  const openPauseModal = (workerId, workerName) => {
    const session = cashRegister.getActiveSession(businessId);
    
    if (!session) {
      console.error('No active session found for pause');
      return;
    }

    setPendingAction({
      type: 'pause',
      workerId,
      workerName,
      sessionId: session.sessionId
    });
    setModalMode('pause');
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setPendingAction(null);
    setModalMode('login');
  };

  // Handle modal confirmation
  const handleModalConfirm = async (data) => {
    try {
      if (!pendingAction) {
        throw new Error('No pending action');
      }

      const { type, workerId, workerName } = pendingAction;

      switch (type) {
        case 'login':
          await handleLogin(workerId, workerName, data.cashAmount ?? data.amount, data.explanation, data);
          // Login: no print — close immediately and continue to POS
          closeModal();
          break;
        case 'logout':
          await handleLogout(workerId, data.cashAmount ?? data.amount, data.explanation, data);
          // Leave modal open for success + print (user closes → Accueil path handled by Role if dual)
          break;
        case 'pause':
          await handlePause(workerId);
          closeModal();
          break;
        default:
          throw new Error('Unknown action type');
      }

      return { success: true };
    } catch (error) {
      console.error('Error handling modal confirmation:', error);
      return { success: false, error: error.message };
    }
  };

  // Handle login
  const handleLogin = async (workerId, workerName, amount, explanation, data = {}) => {
    try {
      const cashAmount = Number.isFinite(Number(data.cashAmount)) ? Number(data.cashAmount) : (Number(amount) || 0);
      const electronicAmount = Number.isFinite(Number(data.electronicAmount)) ? Number(data.electronicAmount) : 0;
      if (businessId && workerId) {
        sessionRecorder.reconcileCashRegisterLockState(businessId);
        reconcileAbandonedWorkerSessions(businessId, workerId);
      }
      pruneStaleWorkerSessions(businessId);
      if (sessionRecorder.isCashRegisterLocked(businessId, workerId)) {
        throw new Error('Cash register is currently locked');
      }
      
      const pauseStatus = sessionRecorder.isCashRegisterPaused(businessId);
      if (pauseStatus) {
        throw new Error('Cash register is paused');
      }

      // Create new session with the current amount (not previous amount)
      const result = cashRegister.createSession(
        workerId, 
        workerName, 
        cashAmount,
        businessId
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      // Update active session state
      setActiveSession(result.session);

      // For login, we don't handle handover deviations since we only consider current amount
      // The explanation parameter is not used for login in the new system
      console.log('Worker login with current amount:', cashAmount, electronicAmount);

      try {
        await sessionRecorder.recordLogin(
          workerId,
          workerName,
          cashAmount,
          businessId,
          'confirm',
          electronicAmount,
          data.amountsByProvider || null
        );
      } catch (e) {
        console.warn('[NewCashRegisterContext] recordLogin:', e?.message || e);
      }

      return result;
    } catch (error) {
      console.error('Error handling login:', error);
      throw error;
    }
  };

  // Handle logout
  const handleLogout = async (workerId, amount, explanation, data = {}) => {
    try {
      const cashAmount = Number.isFinite(Number(data.cashAmount)) ? Number(data.cashAmount) : (Number(amount) || 0);
      const result = cashRegister.endSession(
        workerId, 
        cashAmount, 
        businessId
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      // Clear active session state
      setActiveSession(null);
      setPreviousAmount(cashAmount);

      try {
        const logoutResult = await sessionRecorder.recordLogout(
          workerId,
          cashAmount,
          businessId,
          'confirm',
          explanation || '',
          true,
          {
            electronicAmount: data.electronicAmount,
            totalAmount: data.totalAmount,
            amountsByProvider: data.amountsByProvider || null
          }
        );
        if (logoutResult && logoutResult.success === false) {
          throw new Error(logoutResult.reason || 'recordLogout_failed');
        }
      } catch (e) {
        console.warn('[NewCashRegisterContext] recordLogout:', e?.message || e);
        throw e;
      }

      // Handle deviation if exists
      if (explanation) {
        const deviation = {
          type: 'self',
          amount: parseFloat(amount),
          explanation,
          timestamp: new Date().toISOString()
        };
        
        cashRegister.addDeviation(deviation, businessId);
      }

      return result;
    } catch (error) {
      console.error('Error handling logout:', error);
      throw error;
    }
  };

  // Handle pause
  const handlePause = async (workerId) => {
    try {
      const result = cashRegister.pauseSession(workerId, businessId);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Update active session state
      setActiveSession(result.session);

      return result;
    } catch (error) {
      console.error('Error handling pause:', error);
      throw error;
    }
  };

  // Resume session
  const resumeSession = async (workerId) => {
    try {
      const result = cashRegister.resumeSession(workerId, businessId);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Update active session state
      setActiveSession(result.session);

      return result;
    } catch (error) {
      console.error('Error resuming session:', error);
      throw error;
    }
  };

  // Get session history
  const getSessionHistory = (limit = 50) => {
    return cashRegister.getSessionHistory(businessId, limit);
  };

  // Get business statistics
  const getBusinessStatistics = () => {
    return cashRegister.getBusinessStatistics(businessId);
  };

  // Export session data
  const exportSessionData = (sessionId) => {
    return cashRegister.exportSessionData(sessionId, businessId);
  };

  // Check if cash register is available
  const isCashRegisterAvailable = () => {
    if (!businessId) return false;
    
    const session = cashRegister.getActiveSession(businessId);
    return !session || session.status === 'paused';
  };

  // Check if worker can login
  const canWorkerLogin = (workerId) => {
    if (!businessId) return false;
    
    const session = cashRegister.getActiveSession(businessId);
    return !session || session.workerId === workerId || session.status === 'paused';
  };

  // Get current lot-based inventory for a product
  const getCurrentInventory = (productId, location = 'Principal') => {
    const cacheKey = `${productId}-${location}`;
    
    // Check cache first
    if (lotCacheRef.current.has(cacheKey)) {
      const cached = lotCacheRef.current.get(cacheKey);
      // Cache is valid for 30 seconds
      if (Date.now() - cached.timestamp < 30000) {
        return cached.quantity;
      }
    }
    
    // Get fresh data from lot system
    const quantity = getProductQuantityOptimized(productId, location);
    
    // Update cache without triggering state updates or re-renders during render
    lotCacheRef.current.set(cacheKey, {
      quantity,
      timestamp: Date.now()
    });
    
    return quantity;
  };

  // Get available lots for a product (for FEFO/FIFO logic)
  const getAvailableLots = (productId, location = 'Principal') => {
    const lots = getLotsForProduct(productId, location);
    return lots.filter((lot) => isLotAvailableForSale(lot));
  };

  // Validate sale against current lot state
  const validateSale = (saleProducts) => {
    const errors = [];
    
    for (const saleProduct of saleProducts) {
      const { productId, quantity, stockLocation = 'Principal' } = saleProduct;
      const availableQuantity = getCurrentInventory(productId, stockLocation);
      
      if (quantity > availableQuantity) {
        errors.push({
          productId,
          requested: quantity,
          available: availableQuantity,
          location: stockLocation
        });
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Stable context value — without memo, every parent re-render thrashs Cart +
  // shell consumers (PERF-05).
  const value = useMemo(
    () => ({
      isModalOpen,
      modalMode,
      pendingAction,
      activeSession,
      previousAmount,
      businessId,
      openLoginModal,
      openLogoutModal,
      openPauseModal,
      closeModal,
      handleModalConfirm,
      resumeSession,
      getSessionHistory,
      getBusinessStatistics,
      exportSessionData,
      isCashRegisterAvailable,
      canWorkerLogin,
      getCurrentInventory,
      getAvailableLots,
      validateSale,
      lastLotUpdate
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- capture fns when state changes
    [
      isModalOpen,
      modalMode,
      pendingAction,
      activeSession,
      previousAmount,
      businessId,
      lastLotUpdate
    ]
  );

  return (
    <NewCashRegisterContext.Provider value={value}>
      {children}
    </NewCashRegisterContext.Provider>
  );
};
